import {
  ProcedureExecutionSnapshotSchema,
  type ProcedureExecutionSnapshot,
} from '@erp/shared-types';
import type { Span, SpanActor, SpanProcedureCall } from './types';

/**
 * Pure readers for the trace-detail facts the backend persists beside an action: who acted
 * (plain tool call / procedure / healer), the model's thinking, the action's own arguments, the
 * once-per-replay procedure row, and the main agent's inline `run_procedure` call.
 *
 * Every reader tolerates rows recorded before the field existed — it returns null, and the panel
 * renders exactly what it did before.
 */

/** Must match `SOP_REPLAY_PROCEDURE_TOOL_TYPE` in backend/src/core/services/operator/sopReplayStepTrace.ts. */
export const SOP_REPLAY_PROCEDURE_TOOL_TYPE = 'agent.sop_replay.procedure';

/** The operator's action ids for the main agent's inline procedure call — see `run_procedure.py`. */
export const RUN_PROCEDURE_ACTION = 'run_procedure';
export const RUN_PROCEDURE_RESULT_ACTION = 'run_procedure_result';

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/** The model's extended thinking persisted on an action row's output. */
export function thinkingOf(
  output: Record<string, unknown> | null | undefined,
): string | null {
  return text(output?.thinking);
}

/**
 * Who performed this row, read off the subagent tag the producer stamps on every event a
 * procedure or its healer emits (`inputData.subagent`). The main agent's own call to run a
 * procedure inline is the procedure's too, so it gets the same badge.
 */
export function actorOf(
  input: Record<string, unknown> | undefined,
  action: string | null | undefined,
): SpanActor | null {
  const subagent = input?.subagent;
  if (subagent === 'healer') return 'healer';
  if (subagent === 'procedure') return 'procedure';
  if (action === RUN_PROCEDURE_ACTION || action === RUN_PROCEDURE_RESULT_ACTION)
    return 'procedure';
  return null;
}

/**
 * An operator or replay action's own arguments, projected under `actionArgs` from the canonical
 * step (what was clicked, which key, the typed text). Null when the row carries none.
 */
export function nestedActionArgs(
  input: Record<string, unknown> | undefined,
): Record<string, unknown> | null {
  if (!input) return null;
  return record(input.actionArgs);
}

/**
 * The arguments to show as a span's exact command. A canonical action (`operatorEvidence` with an
 * action index) carries them as its own input; an audit row nests them under `actionArgs`.
 */
export function commandArgsOf(span: Span): Record<string, unknown> | null {
  const args =
    span.operatorEvidence?.actionIndex !== undefined
      ? (span.toolInput ?? null)
      : nestedActionArgs(span.toolInput);
  return args && Object.keys(args).length > 0 ? args : null;
}

/** The procedure row's identity and its plan snapshot, or null when the span isn't one. */
export function readProcedureRow(
  span: Span,
): { ordinal: number; snapshot: ProcedureExecutionSnapshot } | null {
  const input = span.toolInput;
  const parsed = ProcedureExecutionSnapshotSchema.safeParse(
    input?.procedureSnapshot,
  );
  const ordinal = input?.sopProcedureOrdinal;
  if (!parsed.success || typeof ordinal !== 'number') return null;
  if (input?.sopStepIndex !== undefined) return null;
  return { ordinal, snapshot: parsed.data };
}

/** Is this the once-per-replay procedure row (folded into the procedure's own row on display)? */
export function isProcedureRow(span: Span): boolean {
  return readProcedureRow(span) !== null;
}

/**
 * The main agent's inline `run_procedure` call and its result, keyed by the procedure row they
 * belong to, so each can be folded onto the procedure row it started.
 *
 * `keyOf` names that row from the call's span and the replay ordinal it carries. The ordinal alone
 * is not enough: every task of a task loop is its own operator call opening at ordinal 1, so the
 * caller keys by the owning call too (`sopProcedureSpanId`).
 */
export function collectProcedureCalls(
  spans: Span[],
  keyOf: (span: Span, ordinal: number) => string,
): Map<string, SpanProcedureCall> {
  const calls = new Map<string, SpanProcedureCall>();
  const entry = (key: string): SpanProcedureCall => {
    const existing = calls.get(key);
    if (existing) return existing;
    const created: SpanProcedureCall = {
      reasoning: null,
      thinking: null,
      summary: null,
      success: null,
    };
    calls.set(key, created);
    return created;
  };
  for (const span of spans) {
    const args = nestedActionArgs(span.toolInput);
    const ordinal = args?.procedureOrdinal;
    if (typeof ordinal !== 'number') continue;
    if (span.action === RUN_PROCEDURE_ACTION) {
      const call = entry(keyOf(span, ordinal));
      call.reasoning = span.reasoning ?? null;
      call.thinking = span.thinking ?? null;
    } else if (span.action === RUN_PROCEDURE_RESULT_ACTION) {
      const call = entry(keyOf(span, ordinal));
      call.summary = text(args?.summary);
      call.success = typeof args?.success === 'boolean' ? args.success : null;
    }
  }
  return calls;
}

/** A branch step's decision off its boundary row's input. */
export function branchChoiceOf(
  input: Record<string, unknown> | undefined,
): { label: string; mode: string } | null {
  const choice = record(input?.sopBranchChoice);
  const label = text(choice?.label);
  const mode = text(choice?.mode);
  return label && mode ? { label, mode } : null;
}

/**
 * The instructions a parent tool call handed down: the operator `instruction`, the named
 * `procedureName`, and the `procedureParameters` it passed. Null when the call carried none.
 */
export function procedureRequestOf(
  input: Record<string, unknown> | undefined,
): {
  instruction: string | null;
  procedureName: string | null;
  parameters: Array<[string, string]>;
} | null {
  if (!input) return null;
  const instruction = text(input.instruction);
  const procedureName = text(input.procedureName);
  const params = record(input.procedureParameters);
  const parameters = params
    ? Object.entries(params).flatMap(([key, value]): Array<[string, string]> =>
        typeof value === 'string' ? [[key, value]] : [],
      )
    : [];
  if (!instruction && !procedureName && parameters.length === 0) return null;
  return { instruction, procedureName, parameters };
}

/** Scalar argument values rendered for the "what it did" list; nested values as compact JSON. */
export function formatArgValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || value === null)
    return String(value);
  return JSON.stringify(value);
}
